import { useState } from "react";
import { api, type ModalPart, type SelectComponent, type TextInputComponent } from "@shared/api";
import { Dialog, Notice } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { IconCheck } from "@shared/ui/icons";
import { Input } from "@shared/ui/input";
import { Textarea } from "@shared/ui/textarea";
import { useInteractions, type OpenModal } from "../model/interactions";
import { useEntityOptions } from "./BotParts";

/**
 * Окно бота — ответ 9 на команду или нажатие, как модальное окно Discord: поля
 * строкой и абзацем, подписи со списками и просто текст. «Отправить» уходит
 * боту по вызову, на который он открыл окно; прислать его можно один раз.
 *
 * Окно одно на всё приложение: его открывает событие, а не поле ввода, и вызов
 * мог прийти из меню, ленты или ветки.
 */
export function BotModalHost() {
  const open = useInteractions((state) => state.modal);
  if (!open) return null;
  return <BotModal key={open.interactionId} open={open} />;
}

type Answers = Record<string, { value?: string; values?: string[] }>;

/** Поле окна — в ряду (по-старому) или в подписи. */
function fieldOf(part: ModalPart): TextInputComponent | SelectComponent | null {
  if (part.type === 1) return part.components[0] ?? null;
  if (part.type === 18) return part.component;
  return null;
}

function BotModal({ open }: { open: OpenModal }) {
  const close = useInteractions((state) => state.closeModal);
  const [answers, setAnswers] = useState<Answers>(() => {
    const start: Answers = {};
    for (const part of open.modal.components) {
      const field = fieldOf(part);
      if (field?.type === 4 && field.value) start[field.custom_id] = { value: field.value };
      if (field && field.type !== 4) {
        const chosen = field.type === 3
          ? (field.options ?? []).filter((option) => option.default).map((option) => option.value)
          : (field.default_values ?? []).map((item) => item.id);
        if (chosen.length) start[field.custom_id] = { values: chosen };
      }
    }
    return start;
  });
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const set = (customId: string, answer: { value?: string; values?: string[] }) =>
    setAnswers((now) => ({ ...now, [customId]: answer }));

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      const { id } = await api.commands.submit(
        open.interactionId,
        Object.entries(answers).map(([custom_id, answer]) => ({ custom_id, ...answer })),
      );
      close();
      // Ответ на окно ждём, как ответ на команду: «думает…» или «не ответило».
      if (open.bot) useInteractions.getState().sent({ id, channelId: open.channelId, name: "", bot: open.bot });
    } catch {
      setError("Не все поля заполнены как надо, или окно уже отправлено.");
      setSending(false);
    }
  };

  return (
    <Dialog
      title={open.modal.title}
      onClose={close}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={close}>
            Отмена
          </Button>
          <Button type="button" disabled={sending} onClick={() => void submit()}>
            Отправить
          </Button>
        </>
      }
    >
      <form
        className="bot-modal"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {open.modal.components.map((part, index) => {
          if (part.type === 10)
            return (
              <p key={part.id ?? index} className="bot-modal-text">
                {part.content}
              </p>
            );
          const field = fieldOf(part);
          if (!field) return null;
          const label = part.type === 18 ? part.label : field.type === 4 ? field.label : undefined;
          const required = field.required ?? true;
          return (
            <label key={part.id ?? index} className="bot-modal-field">
              <span className="bot-modal-label">
                {label}
                {required && <span className="bot-modal-required"> *</span>}
              </span>
              {part.type === 18 && part.description && <span className="bot-modal-note">{part.description}</span>}
              {field.type === 4 ? (
                field.style === 2 ? (
                  <Textarea
                    rows={4}
                    value={answers[field.custom_id]?.value ?? ""}
                    placeholder={field.placeholder}
                    minLength={field.min_length}
                    maxLength={field.max_length ?? 4000}
                    required={required}
                    onChange={(event) => set(field.custom_id, { value: event.target.value })}
                  />
                ) : (
                  <Input
                    value={answers[field.custom_id]?.value ?? ""}
                    placeholder={field.placeholder}
                    minLength={field.min_length}
                    maxLength={field.max_length ?? 4000}
                    required={required}
                    onChange={(event) => set(field.custom_id, { value: event.target.value })}
                  />
                )
              ) : (
                <ModalChoice
                  select={field}
                  channelId={open.channelId}
                  chosen={answers[field.custom_id]?.values ?? []}
                  onChange={(values) => set(field.custom_id, { values })}
                />
              )}
            </label>
          );
        })}
        {error && (
          <Notice kind="bad" title="Не отправилось">
            {error}
          </Notice>
        )}
      </form>
    </Dialog>
  );
}

/** Список в окне — вариантами прямо в окне: выбор уходит вместе с окном. */
function ModalChoice({
  select,
  channelId,
  chosen,
  onChange,
}: {
  select: SelectComponent;
  channelId: string;
  chosen: string[];
  onChange: (values: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const all = useEntityOptions(select, channelId);
  const wanted = query.trim().toLowerCase();
  const options = (wanted ? all.filter((option) => option.label.toLowerCase().includes(wanted)) : all).slice(0, 25);
  const max = select.max_values ?? 1;
  return (
    <div className="bot-modal-choice" role="listbox" aria-multiselectable={max > 1}>
      {select.type !== 3 && (
        <input className="bot-select-search" value={query} placeholder="Поиск" onChange={(event) => setQuery(event.target.value)} />
      )}
      {options.map((option) => {
        const on = chosen.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={on}
            className={`bot-option${on ? " on" : ""}`}
            disabled={!on && max > 1 && chosen.length >= max}
            onClick={() =>
              onChange(
                max === 1 ? (on ? [] : [option.value]) : on ? chosen.filter((value) => value !== option.value) : [...chosen, option.value],
              )
            }
          >
            <span className="grow">
              <span className="bot-option-label">{option.label}</span>
              {option.description && <span className="bot-option-note">{option.description}</span>}
            </span>
            {on && <IconCheck size={15} />}
          </button>
        );
      })}
    </div>
  );
}
